// CODE-BASED routes (DEV-GUIDE-002 §4 house choice — module subtrees compose
// from packages, so file-based routing's one-app-owns-the-tree assumption
// doesn't hold; code-based hand-writes .lazy() + createLazyRoute).
//
// DOCUMENTED CHOICE — the root layout gates NOTHING. This is doubly load-bearing
// on the OPAC, which is PUBLIC-AWARE: gating is PER-SCREEN, and the two halves of
// the tree gate DIFFERENTLY —
//   · PUBLIC half (the catalogue + the / landing): anonymous is WELCOME. No gate
//     forces a login; the screens read /opac/* and let the SERVER decide anonymous
//     -vs-gated (publicBrowse). A 401/403 from that gate is a legible "sign in to
//     browse" state a screen renders, never a router redirect.
//   · MEMBER half (/account — loans/holds/fines): a session is REQUIRED. The
//     account layout re-checks lib:opac via useSession and bounces anonymous to
//     /login — a defence-in-depth mirror of the API's rbac('lib:opac') guard.
// A single global gate could not express both; keeping the root open is what lets
// the anonymous catalogue and the gated account coexist.
import { createRootRouteWithContext, createRoute, createRouter } from "@tanstack/solid-router";
import type { RouterHistory } from "@tanstack/solid-router";
import type { QueryClient } from "@tanstack/solid-query";
import { NotFoundCard } from "@erp/ui/error-card";
import { parseSearch, stringifySearch } from "@erp/ui/route-kit";
import { App } from "./app";
import { LoginScreen } from "./screens/login";
import { makeOpacRoutes } from "@erp/module-lib/fe/routes/student";
import { makePaymentsRoutes } from "@erp/module-fin/fe/routes/student";
import { makeAuthMemberRoutes } from "@erp/module-auth/fe/routes/student";
import { makeReaderRoutes } from "@erp/module-cms/fe/routes/student";

export interface RouterContext {
  queryClient: QueryClient;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  // <App/> draws the shell chrome and renders the Outlet inside it. It gates
  // NOTHING — it draws chrome for anonymous visitors too, because the OPAC
  // catalogue is public; only /login renders bare.
  component: App,
});

/** The portal's root route type — the parent the OPAC route subtrees hang off of. */
export type StudentRootRoute = typeof rootRoute;

// /login is EAGER — it is the member entry screen; splitting it would add a
// round-trip exactly where first paint matters most.
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginScreen,
});

// / is the lazy chunk — screens/home closes the loop with
// `export const Route = createLazyRoute('/')({ component: HomeScreen })`. It is
// the PUBLIC OPAC landing: it renders for anonymous AND signed-in visitors.
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
}).lazy(() => import("./screens/home").then((m) => m.Route));

// The OPAC composes in here — makeOpacRoutes builds the `/catalogue` (public) and
// `/account` (member-gated) subtrees + their lazy screens against THIS root route,
// so router.tsx stays the single wiring point.
// /verify — where a printed card's QR lands when opened by a plain camera app.
const verifyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/verify",
}).lazy(() => import("./screens/verify").then((m) => m.Route));

// /sign-in-code — the newsletter subscriber's passwordless sign-in. PUBLIC, like
// /login and /verify: the whole point is that the person has no session yet.
const signInCodeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sign-in-code",
}).lazy(() => import("./screens/sign-in-code").then((m) => m.Route));

// /accept-invite — an invitation for someone whose work is HERE (the library).
// No validateSearch: the screen reads two opaque strings off the URL and the
// SERVER decides whether they mean anything, so a client-side schema would only
// turn a bad link into a router error instead of the one refusal sentence.
const acceptInviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/accept-invite",
}).lazy(() => import("./screens/accept-invite").then((m) => m.Route));

// /reset-password — the forgotten-password flow. PUBLIC and chromeless for the
// same reason /login is: the person using it has no session, and getting one back
// is the entire point. The code and the new password are submitted TOGETHER, so
// there is no intermediate "this address was verified" credential to protect.
const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reset-password",
}).lazy(() => import("./screens/reset-password").then((m) => m.Route));

// The reading gate (ADR-010 §2.12) composes in here as two module subtrees:
//   · makeAuthMemberRoutes — /register (chromeless, see app.tsx CHROMELESS)
//     and /verify-email (chromed), the account doors a READER arrives at from
//     the public website; both honour a guarded `?next=`.
//   · makeReaderRoutes — /read/$slug?locale=, the in-browser reader inside the
//     OPAC chrome; the server decides per viewer, the screen maps its answers.
// No nav entry for either (nav.ts untouched): readers come from the website.
const routeTree = rootRoute.addChildren([
  acceptInviteRoute,
  loginRoute,
  resetPasswordRoute,
  indexRoute,
  verifyRoute,
  signInCodeRoute,
  ...makeOpacRoutes(rootRoute),
  ...makePaymentsRoutes(rootRoute),
  ...makeAuthMemberRoutes(rootRoute),
  ...makeReaderRoutes(rootRoute),
]);

/** The unknown-route page: the shared NotFoundCard as the page (h1), with Home as the way out. */
function RouteNotFound() {
  return (
    <NotFoundCard
      id="route-not-found"
      what="page"
      heading
      hint="Check the address, or pick a console from the sidebar."
    />
  );
}

/** Router factory — main.tsx passes the app QueryClient; tests pass a memory history. */
export function createAppRouter(queryClient: QueryClient, history?: RouterHistory) {
  return createRouter({
    routeTree,
    defaultPreload: "intent", // hover/focus warms the chunk (and loader data, when loaders land)
    defaultPreloadStaleTime: 0, // let Query own staleness, not the router (DEV-GUIDE-002 §4.3)
    // An unknown path renders INSIDE the root's outlet — so inside the shell,
    // with the sidebar as the way back — as the same NotFoundCard every detail
    // screen uses, with an h1 and a Home link. TanStack's default was the bare
    // words "Not Found": no heading, no link, no chrome (audit UX-10).
    defaultNotFoundComponent: RouteNotFound,
    // The house search codec (audit UX-17): primitives travel as typed —
    // `?trashed=1`, never `?trashed=%221%22` — so a generated URL and a
    // hand-typed or shared one agree; each route's validateSearch coerces.
    parseSearch,
    stringifySearch,
    context: { queryClient },
    ...(history ? { history } : {}),
  });
}

export type AppRouter = ReturnType<typeof createAppRouter>;

declare module "@tanstack/solid-router" {
  interface Register {
    router: AppRouter;
  }
}
